<template>
    <div 
        :id="cardData.key" 
        class="rounded-xl card-shadow"
        :class="[
            getBaseClass(),
            getSizeContainerClass(),
            getLogoPositionClass(),
            !disableMargins && (cardData.value.position === 'left' ? 'mr-2' : 'ml-2')
        ]"
        :style="getCardBackgroundStyle()"
        @click="handleCardClick"
        v-if="shouldShowCard"
    >
        <!-- Video Background -->
        <video 
            v-if="settings.videos[cardData.key].path"
            :src="settings.videos[cardData.key].path"
            class="video video-background mt-0 px-0 mx-0 rounded-xl"
            :class="[getBaseVideoClass()]"
            autoplay 
            muted 
            loop 
            playsinline 
            preload="auto" 
            controls="false"
        />

        <!-- Image Background -->
        <v-img 
            v-if="settings.backgrounds[cardData.key].path"
            :src="settings.backgrounds[cardData.key].path"
            max-width="100%"
            max-height="100%"
            :class="[
                getBaseCardClass(), 
                getSizeClass()
            ]"
            class="mt-0 px-0 mx-0 rounded-xl"
            :fetchpriority="cardData.number <= 3 ? 'high' : 'low'"
            :loading="cardData.number <= 3 ? 'eager' : 'lazy'"
        />

        <!-- Gallery Component -->
        <GalleryComponentCard
            v-if="hasImages"
            :images="settings.galleries[cardData.key].images"
            :editor="isEditor"
            :explicit="settings.galleries[cardData.key].explicit"
            class="background-media rounded-xl"
            :galleryId="cardData.key"
        />

        <!-- Schedule Component -->
        <ScheduleComponentCard
            v-if="hasSchedule"
            :image="settings.backgrounds[cardData.key].path"
            :schedule="settings.schedules[cardData.key].schedule"
            class="background-media rounded-xl"
            :user="userName"
            :scheduleId="[cardData.key]"
            :colorBackground="settings.schedules[cardData.key].background"
            @click="$emit('save-settings')"
        />

        <!-- Platform Content -->
        <div v-if="showPlatformContent">
            <!-- Platform Name (without slash) -->
            <div 
                v-if="!cardData.value.name.includes('/')"
                :class="cardData.value.color === 1 ? 'white' : 'black'"
            >
                <h1 class="platform-text">{{ cardData.value.name }}</h1>
            </div>

            <!-- Custom Text -->
            <div v-else-if="cardData.value.text">
                <h1 
                    class="platform-text"
                    :class="cardData.value.fonts.font"
                    :style="{
                        color: cardData.value.textColor,
                        fontWeight: cardData.value.fonts.weight,
                        fontSize: cardData.value.fonts.size + 'px'
                    }"
                >
                    {{ cardData.value.text }}
                </h1>
            </div>

            <!-- Platform Logo -->
            <div v-else>
                <img 
                    v-if="cardData.value.name"
                    :src="getPlatformUrl(cardData.value.name)"
                    class="platform-logo"
                    alt="Platform logo"
                />
            </div>
        </div>

        <!-- Editor Placeholder Icon -->
        <v-icon 
            v-if="showEditorIcon"
            center 
            color="#C9C9C9" 
            size="40px"
        >
            mdi-pencil-box
        </v-icon>

        <!-- Error Alert Bubble -->
        <BurbleAlert
            v-if="isEditor && hasError && errorData"
            :message="errorData.error"
            :type="errorData.name"
            :type_error="errorData.type"
            :cardNumber="cardData.number"
            :position="cardData.value.position"
        />
    </div>
</template>

<script>
export default {
    name: 'HarmonyCard',
    
    components: {
        GalleryComponentCard: () => import('@/components/partials/GalleryComponentCard.vue'),
        ScheduleComponentCard: () => import('@/components/partials/ScheduleComponentCard.vue'),
        BurbleAlert: () => import('@/components/harmony/partials/BurbleAlert.vue'),
    },

    props: {
        cardData: {
            type: Object,
            required: true,
            // { key: 'card1', value: {...}, number: 1, size: 'medium', position: 'left' }
        },
        settings: {
            type: Object,
            required: true,
            // site_setting object with platforms, backgrounds, videos, galleries, schedules
        },
        isEditor: {
            type: Boolean,
            default: false
        },
        userName: {
            type: String,
            default: ''
        },
        backendUrl: {
            type: String,
            default: 'https://backend.harmony.fan/storage'
        },
        incompleteConfigurations: {
            type: Array,
            default: () => []
        },
        disableMargins: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        hasImages() {
            const gallery = this.settings.galleries[this.cardData.key];
            return gallery && gallery.images && gallery.images.length > 0;
        },

        hasSchedule() {
            const schedule = this.settings.schedules[this.cardData.key];
            return schedule && schedule.schedule && schedule.schedule.length > 0;
        },

        shouldShowCard() {
            return this.settings.backgrounds[this.cardData.key].path ||
                   this.isEditor ||
                   this.settings.videos[this.cardData.key].path ||
                   this.cardData.value.name ||
                   this.hasSchedule ||
                   this.hasImages;
        },

        showPlatformContent() {
            return this.cardData.value.show && 
                   !this.hasImages && 
                   !this.hasSchedule;
        },

        showEditorIcon() {
            return this.isEditor && 
                   !this.settings.backgrounds[this.cardData.key].path && 
                   !this.hasImages && 
                   !this.hasSchedule;
        },

        hasError() {
            return this.incompleteConfigurations?.some(config => {
                const configCardNumber = this.getCardNumberFromPlatform(config.name);
                return Number(configCardNumber) === Number(this.cardData.number);
            });
        },

        errorData() {
            if (!this.hasError) return null;
            
            const error = this.incompleteConfigurations.find(config => {
                const platformCardNumber = this.getCardNumberFromPlatform(config.name);
                return Number(platformCardNumber) === Number(this.cardData.number);
            });
            
            return error || null;
        }
    },

    methods: {
        handleCardClick() {
            if (this.isEditor) {
                this.$emit('edit-card', {
                    cardKey: this.cardData.key,
                    cardNumber: this.cardData.number,
                    platformName: this.cardData.value.name
                });
            } else {
                const videoOrBackground = this.settings.videos[this.cardData.key].path 
                    ? this.settings.videos[this.cardData.key]
                    : this.settings.backgrounds[this.cardData.key];
                
                this.$emit('navigate-to-card', {
                    card: this.cardData.value,
                    src: videoOrBackground,
                    schedule: this.settings.schedules[this.cardData.key].schedule
                });
            }
        },

        getPlatformUrl(name) {
            return `${this.backendUrl}/platforms/${name}?v=3`;
        },

        getBaseClass() {
            const hasData = this.settings.backgrounds[this.cardData.key].path;
            return this.getBase2(
                this.getClass(this.cardData.value.size), 
                hasData
            );
        },

        getSizeContainerClass() {
            return this.getSizeCard(this.cardData.value.size);
        },

        getSizeClass() {
            const size = this.cardData.value.size;
            if (size === 'large') return 'video';
            if (size === 'medium') return 'card';
            if (size === 'small') return 'card2';
            if (size === 'full-width') return 'card-full'; // NUEVO
            return 'card';
        },
        
        getClass(size) {
            if (size === 'small') return 'card2';
            if (size === 'medium') return 'card';
            if (size === 'large') return 'video';
            if (size === 'full-width') return 'card-full'; // NUEVO
            return 'card';
        },
        
        getSizeCard(size) {
            if (size === 'small') return 'card2-container';
            if (size === 'medium') return 'card-container';
            if (size === 'large') return 'video-container';
            if (size === 'full-width') return 'card-full-container'; // NUEVO
            return 'card-container';
        },

        getBase2(value, data) {
            const lookupTable = {
                'card': {
                    'withData': this.isEditor && !data ? 'base card' : 'base',
                    'noData': 'card base'
                },
                'video': {
                    'withData': this.isEditor && !data ? 'base video' : 'base',
                    'noData': 'video base'
                },
                'card2': {
                    'withData': this.isEditor && !data ? 'base card2' : 'base',
                    'noData': 'card2 base'
                }
            };

            const dataType = data ? 'withData' : 'noData';
            return lookupTable[value]?.[dataType] || 'base';
        },

        getBaseCardClass() {
            const size = this.cardData.value.size;
            return size === 'large' ? 'video' : 'card';
        },

        getBaseVideoClass() {
            return 'base';
        },

        getLogoPositionClass() {
            const platform = this.cardData.value;
            if (platform.size === 'medium' || platform.size === 'large') {
                return `logo-position-${platform.logo_position}`;
            }
            return '';
        },

        getCardBackgroundStyle() {
            const cardSchedule = this.settings.schedules[this.cardData.key];
            const scheduleBg = cardSchedule?.background;

            const hasImage = !!this.settings.backgrounds[this.cardData.key]?.path;
            const hasVideo = !!this.settings.videos[this.cardData.key]?.path;
            const hasGallery = this.hasImages;
            const hasSched = this.hasSchedule;

            if (hasImage || hasVideo || hasGallery || hasSched) {
                return {};
            }

            return {
                backgroundColor: scheduleBg || '#E8E8E8'
            };
        },

        getCardNumberFromPlatform(platformName) {
            const incompleteConfig = this.incompleteConfigurations.find(config =>
                config.name.toLowerCase().includes(platformName.toLowerCase())
            );

            if (incompleteConfig && incompleteConfig.card_number) {
                return incompleteConfig.card_number;
            }

            const allCards = Object.keys(this.settings.platforms);
            for (const cardKey of allCards) {
                const cardPlatform = this.settings.platforms[cardKey]?.name;
                if (cardPlatform && cardPlatform.toLowerCase().includes(platformName.toLowerCase())) {
                    return parseInt(cardKey.replace('card', ''));
                }
            }
            return null;
        }
    }
}
</script>

<style scoped>
.card-container {
    position: relative;
    height: 180px;
}

.video-container {
    position: relative;
    height: 503px;
}

.card2-container {
    position: relative;
    height: 130px;
}

.platform-logo {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    z-index: 0;
}

.platform-text {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 0 10px;
    white-space: normal;
    word-wrap: break-word;
    overflow: hidden;
    font-size: clamp(14px, 5vw, 20px);
    margin: auto;
    line-height: 1.1;
}

.background-media {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    overflow: hidden;
}

.card-shadow {
    overflow: hidden !important;
    box-shadow: #00000061 0px 1px 6px 1.3px;
}

.blur-explicit {
    filter: blur(3.5px);
}

.white h1 {
    color: white !important;
}

.black h1 {
    color: black !important;
}

.base {
    cursor: pointer;
}

.card {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 180px;
}

.video {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 503px;
}

.card2 {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 130px;
}

.video-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
}

.video-background::-webkit-media-controls {
    display: none !important;
}

.logo-position-top .platform-logo,
.logo-position-top .platform-text {
    top: -25% !important;
    bottom: auto !important;
    transform: translateY(0) !important;
}

.logo-position-center .platform-logo,
.logo-position-center .platform-text {
    top: 50% !important;
    bottom: auto !important;
    transform: translateY(-50%) !important;
}

.logo-position-bottom .platform-logo,
.logo-position-bottom .platform-text {
    top: auto !important;
    bottom: -20% !important;
    transform: translateY(0) !important;
}

.card-full-container {
    position: relative;
    height: 180px;
    width: 100%;
}

.card-full {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 180px;
    width: 100%;
}
</style>